import {
  Component,
  computed,
  contentChild,
  Directive,
  forwardRef,
  HostListener,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';
import { OverlayModule } from 'primeng/overlay';
import { Popover } from 'primeng/popover';
import { SharedModule } from 'src/app/core/shared.module';
import { MenuItem } from 'src/app/services/menu/menuItem';
import { VIconButtonComponent } from '../buttons/icon-button/icon-button.component';
import { VKitModule } from '../uikit.module';

/**
 * Marks a projected element as the dropdown's trigger and wires it to
 * toggle the menu popover. Use for triggers the built-in appearances
 * don't cover (labels, chips, avatars):
 *
 * ```html
 * <v-dropdown-menu [menuItems]="items">
 *   <button vDropdownTrigger type="button">10 ▾</button>
 * </v-dropdown-menu>
 * ```
 */
@Directive({
  selector: '[vDropdownTrigger]',
})
export class VDropdownTriggerDirective {
  private readonly dropdown = inject<VDropdownMenuComponent>(
    forwardRef(() => VDropdownMenuComponent)
  );

  @HostListener('click', ['$event'])
  onClick(event: Event): void {
    this.dropdown.popover().toggle(event);
  }
}

@Component({
  selector: 'v-dropdown-menu',
  templateUrl: './dropdown-menu.component.html',
  imports: [
    SharedModule,
    OverlayModule,
    TranslocoModule,
    VIconButtonComponent,
    Popover,
    VKitModule,
  ],
})
export class VDropdownMenuComponent {
  public popover = viewChild.required(Popover);

  readonly menuItems = input.required<MenuItem[]>();

  /** `button` renders the classic bordered icon button; `plain` renders a
   *  bare, borderless trigger (e.g. the "…" at the end of a table row).
   *  For anything else, project a custom trigger with `vDropdownTrigger`
   *  instead of growing this enum. */
  readonly appearance = input<'button' | 'plain'>('button');
  readonly icon = input('pi pi-ellipsis-v');

  /** Projected custom trigger — when present it replaces the built-in
   *  appearances entirely. */
  readonly customTrigger = contentChild(VDropdownTriggerDirective);

  public menuItemsWithCloseHandler = computed(() => {
    return this.menuItems().map(item => ({
      ...item,
      onClicked: item.onClicked
        ? () => {
            item.onClicked!();
            this.popover().hide();
          }
        : undefined,
    }));
  });
}
